import {
  createContext,
  useContext,
  useRef,
  type FC,
  type ReactNode,
} from "react";
import { createStore, useStore, type StoreApi } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { handleSubmit as apiHandleSubmit } from "../apis/project/pipeline";
import type { ProcessResult } from "types/output_vu";
import type { ProcessResultVh } from "types/output_vh";
import axios from "axios";
import { useNotificationStore } from "./notificationStore";

type AnyProcessResult = ProcessResult | ProcessResultVh;

export interface ProgressState {
  step: "idle" | "upload" | "download" | "complete" | "error";
  percent: number;
  message?: string; // 텍스트 메시지 표시용
}

export interface UploadState {
  isLoading: boolean;
  progress: ProgressState;
  result: AnyProcessResult | null;
  error: string | null;
  abortController: AbortController | null;
}

export interface UploadActions {
  handleSubmit: (params: {
    file?: File;
    youtubeUrl?: string;
    projectId: string;
    instrumentsString?: string;
    num_speakers?: number;
  }) => Promise<void>;
  resetState: () => void;
  resetError: () => void;
  cancelUpload: () => void;
}

const initialState: UploadState = {
  isLoading: false,
  progress: { step: "idle", percent: 0 },
  result: null,
  error: null,
  abortController: null,
};

export type UploadStoreState = UploadState & UploadActions;

export const createUploadStore = () =>
  createStore<UploadStoreState>()(
    devtools(
      immer((set, get) => ({
        ...initialState,
        resetState: () => set(initialState),
        resetError: () =>
          set((state) => {
            state.error = null;
            state.progress = { step: "idle", percent: 0 };
          }),
        cancelUpload: () => {
          get().abortController?.abort();
          set((state) => {
            state.progress = { step: "idle", percent: 0 };
            state.isLoading = false;
            state.abortController = null;
            state.result = null;
            state.error = null;
          });
        },
        handleSubmit: async (params) => {
          const controller = new AbortController();
          set((state) => {
            state.isLoading = true;
            state.progress = { step: "upload", percent: 0 };
            state.result = null;
            state.error = null;
            state.abortController = controller;
          });

          try {
            const result = await apiHandleSubmit({
              ...params,
              signal: controller.signal,
              setProgress: (
                progress:
                  | ProgressState
                  | ((prevState: ProgressState) => ProgressState)
              ) =>
                set((state) => {
                  state.progress =
                    typeof progress === "function"
                      ? progress(state.progress)
                      : progress;
                }),
            });
            console.log("📦 UploadStore: Received result from API:", result);
            set((state) => {
              state.result = result;
              state.progress = { step: "complete", percent: 100 };
              state.isLoading = false;
              state.abortController = null;
            });
            console.log("📦 UploadStore: State updated with result");

            // 전역 알림 트리거 (컴포넌트 언마운트 여부와 무관하게 동작)
            if (result && (result as any).documentId) {
              try {
                const { showNotification } = useNotificationStore.getState();
                showNotification({
                  projectId: params.projectId,
                  documentId: (result as any).documentId,
                  projectName: params.projectId,
                  result: result as any,
                });
              } catch (e) {
                console.warn("Failed to trigger global notification:", e);
              }
            }
          } catch (error: any) {
            console.error("Error in handleSubmit:", error);
            if (axios.isCancel(error)) {
              return;
            }
            set((state) => {
              state.progress = { step: "error", percent: 0 };
              state.error = error.message || "An unknown error occurred";
              state.isLoading = false;
              state.abortController = null;
            });
          }
        },
      })),
      { name: "uploadStore" }
    )
  );

export type UploadStoreApi = StoreApi<UploadStoreState>;

const UploadStoreContext = createContext<UploadStoreApi | null>(null);

export const UploadStoreProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const storeRef = useRef<UploadStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createUploadStore();
  }
  return (
    <UploadStoreContext.Provider value={storeRef.current!}>
      {children}
    </UploadStoreContext.Provider>
  );
};

export function useUploadStore<T>(selector: (state: UploadStoreState) => T): T {
  const storeApi = useContext(UploadStoreContext);

  if (!storeApi) {
    throw new Error(`useUploadStore must be used within a UploadStoreProvider`);
  }

  return useStore(storeApi, selector);
}
